// Metric type constants and configurations
export const METRIC_TYPES = {
  NUMERIC: 'numeric',
  LIST: 'list',
  // Future types can be added here:
  // TEXT: 'text',
  // DURATION: 'duration',
  // CATEGORICAL: 'categorical'
};

export const METRIC_TYPE_CONFIGS = {
  [METRIC_TYPES.NUMERIC]: {
    name: 'Numeric',
    description: 'Values with units (hours, kg, etc.)',
    operators: ['=', '>=', '<=', '>', '<', 'range'],
    inputType: 'number',
    validation: validateNumericConfig
  },
  [METRIC_TYPES.LIST]: {
    name: 'List / Custom Options',
    description: 'Define custom options and map to statuses',
    operators: ['operational', 'degraded', 'outage'],
    inputType: 'list',
    validation: validateListConfig
  }
};

// Validation functions for each metric type
export function validateNumericConfig(statusConfig) {
  const errors = [];
  const { operational, degraded, outage } = statusConfig;
  
  // Get all status values
  const operationalData = operational.operator === 'range' 
    ? { type: 'range', min: operational.min, max: operational.max }
    : { type: 'single', operator: operational.operator, value: operational.value };
  
  const degradedData = degraded.operator === 'range' 
    ? { type: 'range', min: degraded.min, max: degraded.max }
    : { type: 'single', operator: degraded.operator, value: degraded.value };
  
  const outageData = outage.operator === 'range' 
    ? { type: 'range', min: outage.min, max: outage.max }
    : { type: 'single', operator: outage.operator, value: outage.value };

  // Check for overlapping ranges/values
  const checkOverlap = (status1, status2, name1, name2) => {
    if (status1.type === 'range' && status2.type === 'range') {
      // Both ranges - check if they overlap
      if (!(status1.max < status2.min || status2.max < status1.min)) {
        errors.push(`${name1} and ${name2} ranges overlap`);
      }
    } else if (status1.type === 'range' && status2.type === 'single') {
      // Range vs single - check if single value is within range
      if (status2.value > status1.min && status2.value <= status1.max) {
        errors.push(`${name2} value (${status2.value}) falls within ${name1} range`);
      }
    } else if (status1.type === 'single' && status2.type === 'range') {
      // Single vs range - check if single value is within range
      if (status1.value >= status2.min && status1.value < status2.max) {
        errors.push(`${name1} value (${status1.value}) falls within ${name2} range`);
      }
    } else {
      // Both single - check if they're equal
      if (status1.value === status2.value) {
        errors.push(`${name1} and ${name2} have the same value (${status1.value})`);
      }
    }
  };

  // Check all combinations
  checkOverlap(operationalData, degradedData, 'Operational', 'Degraded');
  checkOverlap(operationalData, outageData, 'Operational', 'Outage');
  checkOverlap(degradedData, outageData, 'Degraded', 'Outage');

  return errors;
}

export function validateListConfig(statusConfig) {
  const errors = [];
  const { options } = statusConfig;
  
  // Check if there are any options defined
  if (!options || options.length === 0) {
    errors.push('At least one option must be defined');
    return errors;
  }
  
  // Check for duplicate option names
  const optionNames = options.map(opt => opt.name.toLowerCase().trim());
  const uniqueNames = [...new Set(optionNames)];
  if (optionNames.length !== uniqueNames.length) {
    errors.push('Option names must be unique');
  }
  
  // Check for empty option names
  const emptyNames = options.filter(opt => !opt.name.trim());
  if (emptyNames.length > 0) {
    errors.push('Option names cannot be empty');
  }
  
  // Check that at least one status is represented
  const statuses = options.map(opt => opt.status).filter(s => s);
  const uniqueStatuses = [...new Set(statuses)];
  if (uniqueStatuses.length === 0) {
    errors.push('At least one option must have a status assigned');
  }
  
  // Future: Validate that all statuses are valid (when custom statuses are added)
  // This would check against a dynamic list of available statuses
  
  return errors;
}

// Helper function to get default status config for a metric type
export function getDefaultStatusConfig(metricType) {
  switch (metricType) {
    case METRIC_TYPES.NUMERIC:
      return {
        operational: { operator: '>=', value: 7 },
        degraded: { operator: 'range', min: 6, max: 6.9 },
        outage: { operator: '<', value: 6 }
      };
    case METRIC_TYPES.LIST:
      return {
        options: []
      };
    default:
      return {};
  }
}
